import axios from 'axios';
import Config from 'src/config';

import { events } from './events';
import { logger } from './logger';

const log = logger.websocket.bind(logger, 'foxsocket');
const warn = logger.warn.bind(logger, 'foxsocket');
const wsConfig = Config.get('websocket');

function isPublicSubscriptionId(id) {
  return typeof id === 'string' && id.startsWith('g-');
}

let msg = window.msg || {};

class FoxSocket {
  state = 'initializing';
  subscriptions = {};
  wsapiRequests = {};
  wsapiPendingRequests = [];

  waitingForConnected = [];

  waitForConnected = (cb) => {
    if (this.isConnected()) {
      return cb(null);
    } else {
      this.waitingForConnected.push(cb);
      if (this.state !== 'connecting' && this.state !== 'connected') {
        this.connect();
      }
    }
  };

  ensureConnect = (emitOutdated) => {
    log('ensureConnect', { emitOutdated });
    this.forcePing((isConnected) => {
      log('forcePing', isConnected);
      if (!isConnected) {
        if (emitOutdated) events.emit('outdated');
        this.connect();
        // this.emit('refresh:' + this.state.name);
      }
    });
  };

  connect = () => {
    this.wsConfig = wsConfig;
    log('connect', wsConfig.url);
    this._clear();
    Config.set('csId', false);
    this.lastPongDate = Date.now();
    this.state = 'connecting';
    let ws = new WebSocket(wsConfig.url);
    this.ws = ws;
    ws.onopen = this._onopen;
    ws.onmessage = this._onmessage;
    ws.onerror = this._onerror;
    ws.onclose = this._onclose;
  };

  disconnect = () => {
    log('disconnect');
    this._clear();
    this.subscriptions = {};
    this.wsapiRequests = {};
    this.wsapiPendingRequests = [];
  };

  _clear = () => {
    log('_clear');
    this.waitingForcePing = null;
    this.waitingForcePingDate = 0;
    this.lastPongDate = 0;
    this.lastPingDate = 0;
    Config.set('csId', false);
    let prevState = this.state;
    clearInterval(this.pingInterval);
    clearInterval(this.workerInterval);
    clearTimeout(this.reconnectTimeout);
    clearTimeout(this.forcePingTimeout);
    if (this.ws) {
      let fn = function () {};
      this.ws.onopen = fn;
      this.ws.onmessage = fn;
      this.ws.onerror = fn;
      this.ws.onclose = fn;
      this.ws.close();
      this.ws = null;
    }
    this.state = 'disconnected';
    if (prevState === 'authenticated') {
      events.emit('foxsocket:disconnected');
      events.emit('outdated');
    }
  };

  isConnected = () => {
    return this.isAuthenticated() && this.isTransportConnected();
  };

  isAuthenticated = () => {
    return this.state === 'authenticated';
  };

  isTransportConnected = () => {
    let now = Date.now();
    let stateOk = this.state === 'connected' || this.state === 'authenticated';
    let sessionOk = !!Config.get('csId');
    let pingOk = now - this.lastPingDate <= wsConfig.maxPingInterval;
    let pongOk = now - this.lastPongDate <= wsConfig.maxPingInterval;
    log('isTransportConnected', { stateOk, sessionOk, pingOk, pongOk });
    return stateOk && sessionOk && pingOk && pongOk;
  };

  auth = () => {
    let sid = Config.get('sid');
    if (sid) this.ws.send('1&&' + sid);
    return !!sid;
  };

  forcePing = (cb) => {
    clearTimeout(this.forcePingTimeout);
    if (!this.isTransportConnected()) return cb(false);
    log('forcePing start');
    this.waitingForcePingDate = Date.now();
    this.waitingForcePing = cb;
    this._sendPing();
    this.forcePingTimeout = setTimeout(() => {
      if (typeof this.waitingForcePing === 'function') {
        log('forcePing timeout', wsConfig.maxWaitingForcePing);
        this.waitingForcePing(false);
      }
    }, wsConfig.maxWaitingForcePing);
  };

  api = async (subscriptions) => {
    if (!this.isTransportConnected()) {
      return;
    }
    if (!Array.isArray(subscriptions)) subscriptions = [subscriptions];
    if (!subscriptions) {
      log('api no subscriptions', subscriptions);
      return;
    }
    subscriptions = subscriptions.filter((subscription) => this.canSendSubscription(subscription));
    if (subscriptions.length === 0) {
      log('api skip subscriptions', subscriptions);
      return;
    }
    let req = {
      url: this.wsConfig.api + '/wsapi',
      method: 'POST',
      data: {
        subscriptions,
        wsid: Config.get('csId')
      }
    };
    let sid = Config.get('sid');
    if (sid) {
      req.data.sid = sid;
    }
    log('websocketApi', req.url, req.data);
    try {
      await axios(req);
    } catch (err) {
      console.warn('websocketApi err', req, err);
    }
  };

  multi = (subscriptions) => {
    subscriptions = subscriptions.filter((subscription) => {
      let { id } = subscription;
      if (id && subscription.action === 'subscribe' && !this.subscriptions[id]) {
        this.subscriptions[id] = subscription;
        return true;
      } else if (id && subscription.action === 'unsubscribe' && this.subscriptions[id]) {
        delete this.subscriptions[id];
        return true;
      }
      return false;
    });
    if (subscriptions.length > 0) this.api(subscriptions);
  };

  subscribe = (id, data) => {
    if (this.subscriptions[id]) {
      log('already subscribed to', id);
      return;
    } else {
      log('subscribe', id);
    }
    var subscription = { id, action: 'subscribe' };
    if (data) subscription.data = data;
    this.subscriptions[id] = subscription;
    this.api(subscription);
  };

  unsubscribe = (id) => {
    let subscription = this.subscriptions[id];
    if (!subscription) {
      log('unsubscribe fail, not subscribed to', id);
      return;
    } else {
      log('unsubscribe', subscription);
    }
    if (this.state === 'authenticated') {
      this.api({
        ...subscription,
        action: 'unsubscribe'
      });
    }
    delete this.subscriptions[id];
  };

  _worker = () => {
    if (!this.isTransportConnected()) {
      log('_worker state not connected', this.state);
      return;
    }
    if (Date.now() - this.lastPongDate > 1000 * 60 * 5) {
      log('lastPongDate outdated, reconnecting');
      this.lastPongDate = Date.now();
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = setTimeout(this.connect, 5000);
    }
  };

  _sendPing = () => {
    log('ping', Date.now());
    try {
      if (!this.ws) {
        log('_sendPing fail, ws closed', msg);
        return;
      }
      if (!this.isTransportConnected()) {
        log('_sendPing fail, state not connected', msg);
        return;
      }
      this.ws.send('0');
      this.lastPingDate = Date.now();
    } catch (err) {
      log('_send catch', err, msg);
    }
  };

  _onPong = () => {
    log('pong');
  };

  _send = async (msg) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          if (this.state === 'authenticated') this.ws.send(msg);
          resolve();
        } catch (err) {
          console.warn('websocket _send fail', err);
        }
      }, 0);
    });
  };

  // _sendPing = async () => {
  //   await this._send('1&&' + Config.get('sid'));
  // };
  _onAuth = async () => {
    this.state = 'authenticated';
    this._syncSubscriptions('private');
    log('_onAuth connected');
    events.emit('foxsocket:connected');
    let waitingForConnected = this.waitingForConnected;
    if (waitingForConnected.length > 0) {
      this.waitingForConnected = [];
      waitingForConnected.forEach((cb) => {
        if (typeof cb === 'function') {
          console.warn('waitingForConnected succeed');
          cb(null);
        } else {
          console.warn('waitingForConnected error, not a function', cb);
        }
      });
    }

    let pending = this.wsapiPendingRequests;
    let pendingLength = this.wsapiPendingRequests.length;
    if (pendingLength > 0) {
      this.wsapiPendingRequests = [];
      for (let i = 0; i < pendingLength; i++) {
        let apiRequest = this.wsapiRequests[pending[i]];
        if (apiRequest) await this._send('5&&' + JSON.stringify(apiRequest.req));
      }
    }
  };

  _onConnected = (msg) => {
    let csId = msg.join('&&');

    if (!csId) {
      warn('_onConnected missing csId');
      return;
    }

    Config.set('csId', csId);
    this.state = 'connected';
    this.lastPingDate = this.lastPongDate = Date.now();
    this._syncSubscriptions('public');
    this.auth();
  };

  _onopen = () => {
    log('onopen');
    clearInterval(this.pingInterval);
    clearInterval(this.workerInterval);
    this.lastPingDate = this.lastPongDate = Date.now();
    clearInterval(this.pingInterval);
    this.pingInterval = setInterval(this._sendPing, wsConfig.pingInterval);
    this.workerInterval = setInterval(this._worker, 1000 * 60);
  };

  _onmessage = (e) => {
    log('onmessage');
    this.lastPongDate = Date.now();
    if (typeof this.waitingForcePing === 'function') {
      this.waitingForcePing(
        this.lastPongDate - this.waitingForcePingDate <= wsConfig.maxWaitingForcePing
      );
      this.waitingForcePing = null;
    }
    let edata = e.data || '';
    if (edata === '0') return this._onPong();
    log('onmessage data:', edata);
    let msg = edata.split('&&');
    let mid = msg.shift();
    if (mid === '1') {
      this._onAuth(msg);
    } else if (mid === '6') {
      this._onConnected(msg);
    } else if (mid === '0' && msg.length > 0) {
      this._onEvent(msg, edata);
    } else if (mid === '2' && msg.length > 0) {
      this._onWsApi(edata.substring(3));
    } else {
      log('_onmessage error: unknown mid,', edata);
    }
  };

  _onerror = (e) => {
    warn('onerror', e);
  };

  _onclose = (e) => {
    this.lastPongDate = Date.now();
    log('onclose', e);
    this._clear();
    this.reconnectTimeout = setTimeout(this.connect, 5000);
  };

  wsapi = (path, body = {}) => {
    let start = Date.now();
    let id = start + '_' + Math.random();
    let promise = new Promise(function (resolve, reject) {
      let req = {
        id,
        path,
        body
      };
      let apiRequest = {
        id,
        start,
        path,
        body,
        resolve,
        reject,
        req,
        timeout: setTimeout(() => {
          if (this.wsapiRequests[id]) {
            delete this.wsapiRequests[id];
            resolve({ error: 'timeout' });
          }
        }, 1000 * 60)
      };
      this.wsapiRequests[id] = apiRequest;
      if (this.state === 'authenticated') {
        this.ws.send('5&&' + JSON.stringify(req));
      } else {
        this.wsapiPendingRequests.push(id);
      }
      promise.cancel = function () {
        clearTimeout(this.wsapiRequests[id].timeout);
        delete this.wsapiRequests[id];
        this.wsapiPendingRequests = this.wsapiPendingRequests.filter((item) => item !== id);
        resolve({ error: 'canceled' });
      };
    });
    return promise;
  };

  _onEvent = (msg, edata) => {
    var [channel, event, date, data] = msg;
    var parsed = false;
    try {
      data = JSON.parse(data);
      parsed = true;
    } catch (err) {
      warn('_onmessage data parse error', edata, err);
    }
    if (parsed) {
      events.emit('foxsocket:message:' + event, { channel, date, data });
      events.emit('ws:' + event, data);
    }
  };

  _onWsApi = (msg) => {
    log('_onWsApi', msg);
    try {
      let { id, body } = JSON.parse(msg);
      let apiRequest = this.wsapiRequests[id];
      if (apiRequest) {
        let { resolve, timeout } = apiRequest;
        clearTimeout(timeout);
        let result = {
          error: (body && body.error) || null,
          data: body && typeof body.data !== 'undefined' ? body.data : null
          // time: (Date.now() - startTime) / 1000 + 's'
        };
        if (!result.error && typeof body.data === 'undefined') result.error = 'no data received';
        delete this.wsapiRequests[id];
        resolve(result);
      } else {
        warn('_onWsApi err: request not found', msg);
      }
    } catch (err) {
      warn('_onWsApi err:', err);
    }
  };

  canSendSubscription = (subscription) => {
    if (!subscription?.id || !this.isTransportConnected()) {
      return false;
    }

    if (isPublicSubscriptionId(subscription.id)) {
      return true;
    }

    return this.isAuthenticated();
  };

  _syncSubscriptions = (type) => {
    let subscriptions = Object.keys(this.subscriptions)
      .map((key) => this.subscriptions[key])
      .filter((subscription) => {
        if (type === 'public') {
          return isPublicSubscriptionId(subscription.id);
        }

        if (type === 'private') {
          return !isPublicSubscriptionId(subscription.id);
        }

        return true;
      })
      .map((subscription) => ({
        ...subscription,
        action: 'subscribe'
      }));

    if (subscriptions.length > 0) {
      this.api(subscriptions);
    }
  };
}

const fox = new FoxSocket();
export const foxsocket = function () {
  return fox;
};

if (Config.get('env') === 'testing') {
  window.lifeapp = window.lifeapp || {};
  window.lifeapp.foxsocket = window.lifeapp.foxsocket || foxsocket;
}
